/**
 * Settings page — notifications + consent management.
 * (Per user spec — no language toggle in v1, dark mode removed in V14.3.)
 */
import { useEffect, useState } from 'react';
import api from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Switch } from '../components/ui/switch';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import {
  Bell, ShieldCheck, UserMinus,
} from 'lucide-react';
import { useAuth } from '../lib/auth-context';

export default function LearnerSettingsPage() {
  return (
    <div className="space-y-6 max-w-3xl mx-auto" data-testid="settings-page">
      <header>
        <h1 className="font-heading text-3xl">Settings</h1>
        <p className="text-sm text-muted-foreground mt-1">Personalise your Soul Sanskar experience.</p>
      </header>

      <NotificationsSection />
      <ConsentSection />
    </div>
  );
}

// ─────────────────────────── Notifications ───────────────────────────

function NotificationsSection() {
  const { user } = useAuth();
  const profile = user?.pregnancy_profile || {};
  const isPostnatal = (
    profile.stage === 'postnatal'
    || profile.course_type === 'postnatal'
    || !!profile.delivery_date
  );
  const [prefs, setPrefs] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/api/notifications/preferences')
      .then(r => setPrefs(r.data || {}))
      .catch(() => setPrefs({}))
      .finally(() => setLoading(false));
  }, []);

  const toggle = async (key, val) => {
    const next = { ...prefs, [key]: val };
    setPrefs(next);
    try {
      await api.put('/api/notifications/preferences', next);
    } catch {
      toast.error('Could not save preferences');
    }
  };

  if (loading) return null;

  const items = [
    { key: 'class_reminder_email', label: 'Class reminder emails', desc: '1 hour before live classes' },
    { key: 'class_reminder_push', label: 'Class reminder push', desc: 'On-device notifications' },
    { key: 'doctor_relay_email', label: 'Doctor reply emails', desc: 'When your doctor responds' },
    {
      key: 'soulai_daily_tip',
      label: 'SoulAI daily tip',
      // V14.1 — postnatal-aware copy
      desc: isPostnatal
        ? 'Personalised postnatal tip each morning'
        : 'Personalised pregnancy tip each morning',
    },
    { key: 'product_updates', label: 'Product updates', desc: 'New courses, instructors, features' },
  ];

  return (
    <Card data-testid="notifications-section">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-lg"><Bell className="h-5 w-5" /> Notifications</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {items.map(({ key, label, desc }) => (
          <div key={key} className="flex items-center justify-between">
            <div className="flex-1 min-w-0">
              <p className="font-medium">{label}</p>
              <p className="text-xs text-muted-foreground">{desc}</p>
            </div>
            <Switch
              checked={prefs[key] !== false}
              onCheckedChange={(v) => toggle(key, v)}
              data-testid={`pref-${key}`}
            />
          </div>
        ))}
      </CardContent>
    </Card>
  );
}

// ─────────────────────────── (Stage flip moved to ProfilePage tab) ───────────────────────────

// ─────────────────────────── Consent management ───────────────────────────

function ConsentSection() {
  const [consent, setConsent] = useState(null);
  const [revoking, setRevoking] = useState(false);

  useEffect(() => {
    api.get('/api/doctor-relay/consent/status')
      .then(r => setConsent(r.data))
      .catch(() => setConsent({}));
  }, []);

  const revoke = async () => {
    if (!consent?.doctor_id) return;
    setRevoking(true);
    try {
      await api.post('/api/doctor-relay/consent', { doctor_id: consent.doctor_id, granted: false });
      toast.success('Data sharing revoked.');
      setConsent({ ...consent, consent_given: false });
    } catch {
      toast.error('Could not revoke');
    }
    setRevoking(false);
  };

  if (!consent || !consent.has_assigned_doctor) return null;

  return (
    <Card data-testid="consent-section">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-lg">
          <ShieldCheck className="h-5 w-5" /> Privacy & Data Sharing
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="flex items-center justify-between">
          <div>
            <p className="font-medium">Sharing data with Dr. {consent.doctor_name}</p>
            <p className="text-xs text-muted-foreground">{consent.specialization}</p>
            {consent.consent_given && (
              <Badge variant="secondary" className="mt-1 bg-emerald-100 text-emerald-800">Granted {consent.granted_at ? new Date(consent.granted_at).toLocaleDateString() : ''}</Badge>
            )}
          </div>
          {consent.consent_given ? (
            <Button variant="outline" onClick={revoke} disabled={revoking} data-testid="revoke-consent-button">
              <UserMinus className="h-4 w-4 mr-2" /> Revoke
            </Button>
          ) : (
            <Badge variant="outline">Not granted</Badge>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
